import React, { useEffect, useRef } from "react";
import { Mail, MessageCircle, MessageSquareText } from "lucide-react";
import { links } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

export const Reveal = ({ as: Tag = "div", className = "", delay = 0, children, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.classList.add("in");
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
};

export const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-[1346px] px-5 md:px-8 lg:w-[86%] lg:px-0 ${className}`}>{children}</div>
);

export const EmailPill = ({ className = "", dark = false }) => {
  const { content, t } = useSite();
  const list = [content?.email, content?.email2].filter(Boolean);
  if (!list.length) return null;
  return (
    <span className={`inline-flex max-w-full flex-wrap gap-2 ${className}`}>
      {list.map((em, i) => <a key={em}
      href={`mailto:${em}`}
      className={`inline-flex max-w-full items-center gap-2 rounded-full border px-4 py-2 text-[14.5px] font-semibold transition-colors ${dark ? "border-white/25 bg-white/10 text-white hover:bg-white/20" : "border-[#bfe7de] bg-white text-[#0b6f65] shadow-[0_6px_18px_-10px_rgba(14,140,127,.5)] hover:bg-[#effaf7]"} `}
      data-testid={i ? "email-pill-2" : "email-pill"}
    >
      <Mail size={16} className="shrink-0" />
      <span className="truncate">{t("Email us:")} <span className="underline decoration-[#0e8c7f]/40 underline-offset-2">{em}</span></span>
    </a>)}
    </span>
  );
};

export const SectionHead = ({ eyebrow, title, text, center = true }) => (
  <Reveal className={center ? "mx-auto max-w-[640px] text-center" : "max-w-[640px]"}>
    <div className="sw-section-eyebrow">{eyebrow}</div>
    <h2 className="sw-h2 mt-3">{title}</h2>
    {text && <p className="mt-4 text-[17px] leading-relaxed text-[#4b5d5a]">{text}</p>}
    <ContactPills center={center} />
  </Reveal>
);

export const ContactPills = ({ center = true, className = "mt-5" }) => {
  const { content, openChat, t } = useSite();
  const l = links(content);
  const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[14px] font-semibold transition-colors";
  return (
    <div className={`${className} flex flex-col gap-2.5 ${center ? "items-center" : "items-start"}`} data-testid="contact-pills">
      <div className={`flex flex-wrap gap-2 ${center ? "justify-center" : ""}`}>
        <button onClick={() => openChat()} className={`${btn} bg-[#0e8c7f] text-white hover:bg-[#0b7569] shadow-[0_6px_16px_-8px_rgba(14,140,127,.8)]`} data-testid="pill-live-chat">
          <span className="relative flex"><MessageCircle size={15} /><span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#4ade80]" /></span>{t("Live Chat")}
        </button>
        <a href={l.sms} className={`${btn} border border-[#0f2e2a] text-[#0f2e2a] hover:bg-[#0f2e2a] hover:text-white`} data-testid="pill-text">
          <MessageSquareText size={15} />{t("Text me")}
        </a>
        <a href={l.wa} target="_blank" rel="noreferrer" className={`${btn} bg-[#25a35a] text-white hover:bg-[#1e8a4b]`} data-testid="pill-whatsapp">
          <MessageCircle size={15} />{t("WhatsApp me")}
        </a>
      </div>
      <EmailPill />
    </div>
  );
};
